import IntlMessageFormat from 'intl-messageformat';

import { langStore } from "~/stores/lang";
import { ADMIN_LANG } from '~/admin-lang-const';
import { ITranslationArguments } from '~/interfaces';
import { MIN_STRING_LENGTH, PENNY_KC_RATIO } from '~/const';
import { formatNumberToThousands, getReportCallTitle, pricePerCreditBE2FE } from '~/utils/utils';
import { IClientProjectCatalog, IClientProjectInvitation } from '~/providers/client-interfaces';
import { TRestrictionsTypes, TInternalUserRole, TUserKeyRole, TProjectBusinessPlan, TProjectBillingMethod, IProjectBillingHistory, IReportItem, ITransactionApproval } from '~/providers/interfaces';
import { IAdminCatalogsFilter, IAdminJournalItem, IAdminSearchProject, IAdminSearchProjectFilter, IProjectNotification, IQuickCommentItem } from '~/providers/admin-interfaces';

export type TTrans = (key: string, arg?: ITranslationArguments) => string;

const getTrans = (source: any) => (key: string, arg?: ITranslationArguments): string => {
	if (key in ADMIN_LANG) {
		// @ts-ignore
		return new IntlMessageFormat(ADMIN_LANG[key], "cs").format(arg) as string;
	}

	const i18nData = source as any;

	return key in i18nData ? i18nData[key](arg) : key;
};

const transInternalRole = (trans: TTrans) => (role: TInternalUserRole): string => {
	if (role === "internal_admin") {
		return trans("createInternalUserModal.internalRole.internalAdmin");
	} else if (role === "salesman") {
		return trans("createInternalUserModal.internalRole.salesman");
	}

	// def. zadna
	return trans("createInternalUserModal.internalRole.none");
};

const transUserRole = (trans: TTrans) => (role: TUserKeyRole): string => {
	switch (role) {
		case "admin":
			return trans(`userRole.admin`);

		case "owner":
			return trans(`userRole.owner`);

		case "visitor":
			return trans(`userRole.visitor`);

		default:
	}

	return "";
};

const transInvitationState = (trans: TTrans) => (invitationData: IClientProjectInvitation): string => {
	let state = trans("userList.state.sended");

	if (invitationData.confirmed) {
		state = trans("userList.state.confirmed");
	} else if (invitationData.expired) {
		state = trans("userList.state.expired");
	} else if (invitationData.refused) {
		state = trans("userList.state.refused");
	}

	return state;
};

const transRestrictionsPlaceholder = (trans: TTrans) => (type: TRestrictionsTypes): string => {
	switch (type) {
		case "ipAddresses":
			return trans(`restrictionsPage.placeholder.ipAddresses`);

		case "referers":
			return trans("restrictionsPage.placeholder.userAgents", { count: MIN_STRING_LENGTH });

		case "userAgents":
			return trans("restrictionsPage.placeholder.userAgents", { count: MIN_STRING_LENGTH });

		default:
	}

	return "";
};

const transProjectBusinessPlan = (trans: TTrans) => (type: TProjectBusinessPlan): string => {
	switch (type) {
		case "advanced":
			return trans("createProject.businessPlan.advanced");

		case "basic":
			return trans("createProject.businessPlan.basic");

		case "technical":
			return trans("createProject.businessPlan.technical");

		case "vip":
			return trans("createProject.businessPlan.vip");

		case "internal":
			return trans("createProject.businessPlan.internal");

		default:
	}

	return "";
};

const getSemaphoreTitle = (trans: TTrans) => (consumptionTime: number, date: string | null): string => {
	if (date) {
		const dateObj = new Date(date);
		const curObj = new Date();

		curObj.setDate(curObj.getDate() - consumptionTime);

		if (dateObj.getTime() >= curObj.getTime()) {
			return trans("semaphore.green", { count: consumptionTime });
		}

		return trans("semaphore.orange", { count: consumptionTime });
	}

	return trans("semaphore.red");
};

const formatCredits = (trans: TTrans) => (count: number, useAutomatic?: boolean) => count === 0 && useAutomatic
	? trans("creditsZero")
	: trans("credits", {
		formattedCount: formatNumberToThousands(count),
		count,
	});

const formatPrice = (price: number, withCurrency = true) => `${formatNumberToThousands(price)}${withCurrency ? " Kč" : ""}`;

// cena je v halirich
const formatPennyPrice = (price: number, withCurrency = true) => formatPrice(price / PENNY_KC_RATIO, withCurrency);

const formatPricePerCredit = (price: number, withCurrency = true) => formatPrice(pricePerCreditBE2FE(price), withCurrency);

const transBillingMethod = (trans: TTrans) => (method: TProjectBillingMethod): string => {
	switch (method) {
		case "none":
			return trans("projectFinancialPage.paidType.none");

		case "sos":
			return trans("projectFinancialPage.paidType.sos");

		case "wallet":
			return trans("projectFinancialPage.paidType.wallet");

		case "barter":
			return trans("projectFinancialPage.paidType.barter");

		default:
	}

	return "";
};

const transBillingMethodShort = (trans: TTrans) => (method: TProjectBillingMethod): string => {
	switch (method) {
		case "none":
			return trans("createProject.billingMethod.none");

		case "sos":
			return trans("createProject.billingMethod.sos");

		case "wallet":
			return trans("createProject.billingMethod.wallet");

		case "barter":
			return trans("createProject.billingMethod.barter");

		default:
	}

	return "";
};

const transBillingHistoryState = (trans: TTrans) => (billingHistory: IProjectBillingHistory): string => {
	switch (billingHistory.transactionState) {
		// kompletni
		case "completed":
			return billingHistory.billingMethod === "none"
				? trans("transactionState.completedFree")
				// uhrazeno - cena 0 -> Zdarma
				: billingHistory.amountTotal === 0
					? trans("transactionState.completedPaidFree")
					: trans("transactionState.completedPaid");

		// castecna
		case "partial":
			return billingHistory.billingMethod === "sos"
				? trans("transactionState.partialSOS")
				: trans("transactionState.partialWallet");

		// neuzavreno
		case "unprocessed":
			return trans("transactionState.unprocessed");

		default:
	}

	return "";
};

const transJournalAction = (trans: TTrans) => (action: IAdminJournalItem["action"]): string => {
	switch (action) {
		case "DELETE":
			return trans("adminJournalsTable.roleAction.delete");

		case "INSERT":
			return trans("adminJournalsTable.roleAction.insert");

		case "UPDATE":
			return trans("adminJournalsTable.roleAction.update");

		default:
	}

	return "";
};

const transHistoryType = (trans: TTrans) => (type: IProjectNotification["notificationType"]): string => {
	switch (type) {
		case "BILLING_DONE":
			return trans("adminProjectNotifications.type.billingDone");

		case "DEBT":
			return trans("adminProjectNotifications.type.debt");

		case "DEBT_PAID":
			return trans("adminProjectNotifications.type.debtPaid");

		case "SOFT_LIMIT":
			return trans("adminProjectNotifications.type.softLimit");

		case "HARD_LIMIT":
			return trans("adminProjectNotifications.type.hardLimit");

		case "ADMIN_SWITCH_OFF":
			return trans("adminProjectNotifications.type.adminSwitch_off");

		case "ADMIN_SWITCH_ON":
			return trans("adminProjectNotifications.type.adminSwitch_on");

		case "USER_SWITCH_OFF":
			return trans("adminProjectNotifications.type.userSwitch_off");

		case "USER_SWITCH_ON":
			return trans("adminProjectNotifications.type.userSwitch_on");

		case "FREE_CONSUMPTION_LIMIT":
			return trans("adminProjectNotifications.type.freeConsumptionLimit");

		case "NOT_ENOUGH_WALLET_CREDIT":
			return trans("adminProjectNotifications.type.notEnoughWalletCredit");

		case "ADVANCED_CONSENT_NOT_VALID":
			return trans("adminProjectNotifications.type.advancedConsentNotValid");

		case "ADVANCED_TO_BASIC_DOWNGRADE":
			return trans("adminProjectNotifications.type.advancedToBasicDowngrade");

		case "EXPIRATION":
			return trans("adminProjectNotifications.type.expiration");

		case "BULK":
			return trans("adminProjectNotifications.type.bulk");

		case "CATALOG_PUBLISHABLE":
			return trans("adminProjectNotifications.type.catalogPublishable");

		default:
	}

	return "";
};

const getFilterAscDesc = (trans: TTrans, isAsc: boolean) => isAsc
	? trans("order.asc")
	: trans("order.desc");

const filterSearchProjectsDesc = (trans: TTrans) => (stateFilter: IAdminSearchProjectFilter, count: number) => {
	const desc = [];
	const project = [];

	if (stateFilter.keyword) {
		desc.push(`hledat "${stateFilter.keyword}"`);
	}

	if (stateFilter.businessPlanType) {
		const msg = [];
		const parts = stateFilter.businessPlanType.split(",");

		parts.forEach(part => {
			switch (part) {
				case "basic":
					msg.push(trans("projectFilter.basic"));
					break;

				case "advanced":
					msg.push(trans("projectFilter.advanced"));
					break;

				case "vip":
					msg.push(trans("projectFilter.vip"));
					break;

				case "technical":
					msg.push(trans("projectFilter.technical"));
					break;

				case "internal":
					msg.push(trans("projectFilter.internal"));
					break;

				default:
			}
		});

		project.push(msg.join(", "));
	}

	if (stateFilter.billingMethod) {
		let msg = "";

		switch (stateFilter.billingMethod) {
			case "none":
				msg = trans("projectFilter.none");
				break;

			case "wallet":
				msg = trans("projectFilter.wallet");
				break;

			case "sos":
				msg = trans("projectFilter.sos");
				break;

			case "barter":
				msg = trans("projectFilter.barter");
				break;

			default:
		}

		project.push(trans("projectFilter.billingMethod", { billingMethod: msg }));
	}

	if ("billedConsumption" in stateFilter) {
		project.push(stateFilter.billedConsumption ? trans("projectFilter.billedConsumptionOn") : trans("projectFilter.billedConsumptionOff"));
	}

	if ("canConsume" in stateFilter) {
		project.push(stateFilter.canConsume ? trans("projectFilter.canConsumeOn") : trans("projectFilter.canConsumeOff"));
	}

	if ("approvedAdvancedPlan" in stateFilter) {
		project.push(stateFilter.approvedAdvancedPlan ? trans("projectFilter.approvedAdvancedPlanOn") : trans("projectFilter.approvedAdvancedPlanOff"));
	}

	if (stateFilter.actionRequired) {
		project.push(trans("projectFilter.actionRequired"));
	}

	if (stateFilter.vip0) {
		project.push(trans("projectFilter.vip0"));
	}

	if ("archived" in stateFilter) {
		project.push(stateFilter.archived ? trans("projectFilter.archived.on") : trans("projectFilter.archived.off"));
	}

	if (project.length) {
		desc.push(`${project.join(" ")} ${trans("project")}`);
	}

	if ("sortByInternalName" in stateFilter) {
		desc.push(trans("projectFilter.sortByInternalName", { sort: getFilterAscDesc(trans, stateFilter.sortByInternalName === "ASC") }));
	}

	if ("sortByCanConsume" in stateFilter) {
		desc.push(trans("projectFilter.sortByCanConsume", { sort: getFilterAscDesc(trans, stateFilter.sortByCanConsume === "ASC") }));
	}

	if ("sortByBusinessPlanType" in stateFilter) {
		desc.push(trans("projectFilter.sortByBusinessPlanType", { sort: getFilterAscDesc(trans, stateFilter.sortByBusinessPlanType === "ASC") }));
	}

	if ("sortByBilledConsumption" in stateFilter) {
		desc.push(trans("projectFilter.sortByBilledConsumption", { sort: getFilterAscDesc(trans, stateFilter.sortByBilledConsumption === "ASC") }));
	}

	if ("sortByCurrentMonthConsumption" in stateFilter) {
		desc.push(trans("projectFilter.sortByCurrentMonthConsumption", { sort: getFilterAscDesc(trans, stateFilter.sortByCurrentMonthConsumption === "ASC") }));
	}

	if ("sortByPreviousMonthConsumption" in stateFilter) {
		desc.push(trans("projectFilter.sortByPreviousMonthConsumption", { sort: getFilterAscDesc(trans, stateFilter.sortByPreviousMonthConsumption === "ASC") }));
	}

	if ("sortByLastConsumptionDate" in stateFilter) {
		desc.push(trans("projectFilter.sortByLastConsumptionDate", { sort: getFilterAscDesc(trans, stateFilter.sortByLastConsumptionDate === "ASC") }));
	}

	if ("sortByCurrentMonthAmountBilled" in stateFilter) {
		desc.push(trans("projectFilter.sortByCurrentMonthAmountBilled", { sort: getFilterAscDesc(trans, stateFilter.sortByCurrentMonthAmountBilled === "ASC") }));
	}

	if ("sortByPreviousMonthAmountBilled" in stateFilter) {
		desc.push(trans("projectFilter.sortByPreviousMonthAmountBilled", { sort: getFilterAscDesc(trans, stateFilter.sortByPreviousMonthAmountBilled === "ASC") }));
	}

	if (!desc.length) {
		desc.push(trans("projectFilter.noLimit"));
	}

	desc.push(trans("projectsCount", { count }));

	return desc.join(", ");
};

const transCatalogQuality = (trans: TTrans) => (quality: IClientProjectCatalog["quality"]) => {
	switch (quality) {
		case "vip":
			return trans("adminProjectCatalogModal.quality.value.vip");

		case "interesting":
			return trans("adminProjectCatalogModal.quality.value.interesting");

		case "neutral":
			return trans("adminProjectCatalogModal.quality.value.neutral");

		case "uninteresting":
			return trans("adminProjectCatalogModal.quality.value.uninteresting");

		case "undetermined":
			return trans("adminProjectCatalogModal.quality.value.undetermined");

		default:
	}

	return "";
};

const filterCatalogsDesc = (trans: TTrans) => (stateFilter: IAdminCatalogsFilter, count: number) => {
	const desc = [];

	if (stateFilter.keyword) {
		desc.push(`hledat "${stateFilter.keyword}"`);
	}

	if ("published" in stateFilter) {
		desc.push(stateFilter.published ? trans("catalogFilter.publishedOn") : trans("catalogFilter.publishedOff"));
	}

	if (stateFilter.quality) {
		desc.push(transCatalogQuality(trans)(stateFilter.quality));
	}

	if (stateFilter.approvedByAdmin) {
		let msg = "";

		switch (stateFilter.approvedByAdmin) {
			case "approved":
				msg = trans("catalogFilter.approvedByAdmin.approved");
				break;

			case "rejected":
				msg = trans("catalogFilter.approvedByAdmin.rejected");
				break;

			case "undetermined":
				msg = trans("catalogFilter.approvedByAdmin.undetermined");
				break;

			default:
		}

		desc.push(trans("catalogFilter.approvedByAdmin.title", { msg }));
	}

	// razeni
	if ("sortByLastUpdate" in stateFilter) {
		desc.push(trans("catalogFilter.sortByLastUpdate", { sort: getFilterAscDesc(trans, stateFilter.sortByLastUpdate === "ASC") }));
	}

	if ("sortByQuality" in stateFilter) {
		desc.push(trans("catalogFilter.sortByQuality", { sort: getFilterAscDesc(trans, stateFilter.sortByQuality === "ASC") }));
	}

	if ("sortByPublished" in stateFilter) {
		desc.push(trans("catalogFilter.sortByPublished", { sort: getFilterAscDesc(trans, stateFilter.sortByPublished === "ASC") }));
	}

	if ("sortByApproved" in stateFilter) {
		desc.push(trans("catalogFilter.sortByApproved", { sort: getFilterAscDesc(trans, stateFilter.sortByApproved === "ASC") }));
	}

	if ("sortByInternalName" in stateFilter) {
		desc.push(trans("catalogFilter.sortByInternalName", { sort: getFilterAscDesc(trans, stateFilter.sortByInternalName === "ASC") }));
	}

	if ("sortByPreviousMonthConsumption" in stateFilter) {
		desc.push(trans("catalogFilter.sortByPreviousMonthConsumption", { sort: getFilterAscDesc(trans, stateFilter.sortByPreviousMonthConsumption === "ASC") }));
	}

	if (!desc.length) {
		desc.push(trans("projectFilter.noLimit"));
	}

	desc.push(trans("adminCatalogsPage.catalogsCount", { count }));

	return desc.join(", ");
};

const bussinessPlanToShortcut = () => (value: TProjectBusinessPlan) => {
	switch (value) {
		case "basic":
			return "ZÁK";

		case "advanced":
			return "ZVÝH";

		case "internal":
			return "INT";

		case "technical":
			return "TECH";

		case "vip":
			return "MÍRU";

		default:
	}

	return "";
};

const paymentMethodToShortcut = () => (value: TProjectBillingMethod) => {
	switch (value) {
		case "none":
			return "ŽÁD";

		case "barter":
			return "BAR";

		case "sos":
			return "SOS";

		case "wallet":
			return "PEN";

		default:
	}

	return "";
};

const getVIPInfo = () => (project: IAdminSearchProject) => {
	const pricePerCredit = formatPricePerCredit(project.pricePerCredit, false);
	const fixedBillingAmount = formatPennyPrice(project.fixedBillingAmount, false);
	const freeCreditsPerMonth = formatNumberToThousands(project.freeCreditsPerMonth);

	return `(${[pricePerCredit, fixedBillingAmount, freeCreditsPerMonth].join(" / ")})`;
};

const graphLegendTrans = () => (name: keyof IReportItem) => {
	switch (name) {
		case "amountTotal":
			return "Celkem";

		case "amountViaSos":
			return "SOS";

		case "amountViaBarter":
			return "Barter";

		case "amountViaWallet":
			return "Peněženka";

		case "newProjectCount":
			return "Nové projekty";

		case "projectsWithConsumption":
			return "Všechny projekty se spotřebou";

		case "projectsBasicWithConsumption":
			return "Základní projekty se spotřebou";

		case "projectsAdvancedWithConsumption":
			return "Zvýhodněné projekty se spotřebou";

		case "projectsVipWithConsumption":
			return "VIP projekty se spotřebou";

		case "totalConsumedCredits":
			return "Celkem kreditů";

		case "creditsFreeOfCharge":
			return "Kreditů zdarma";

		case "creditsCharged":
			return "Placených kreditů";

		case "totalResourceCalls":
			return "Celkové volání";

		default:
			if (/^total.*Calls$/u.test(name as string)) {
				return getReportCallTitle(name as string);
			}
	}

	return name;
};

const transApprovalReason = () => (reason: ITransactionApproval["approvalReason"]) => {
	switch (reason) {
		case "debt":
			return "dluh";

		case "limit":
			return "limit";

		default:
	}

	return "";
};

const transCatalogApproved = (trans: TTrans) => (state: IClientProjectCatalog["approvedByAdmin"]) => {
	if (state === null) {
		return trans("adminProjectCatalogModal.approvedByAdmin.value.null");
	}

	return state
		? trans("adminProjectCatalogModal.approvedByAdmin.value.true")
		: trans("adminProjectCatalogModal.approvedByAdmin.value.false");
};

const transQuickCommentType = (trans: TTrans) => (type: IQuickCommentItem["type"]) => {
	switch (type) {
		case "api-key-id":
			return trans("adminQuickCommentsTable.type.apiKeyId");

		case "api-key-name":
			return trans("adminQuickCommentsTable.type.apiKeyName");

		case "email":
			return trans("adminQuickCommentsTable.type.email");

		default:
	}

	return "";
};

function getExport(trans: TTrans) {
	return {
		trans,
		transInternalRole: transInternalRole(trans),
		transUserRole: transUserRole(trans),
		transInvitationState: transInvitationState(trans),
		transRestrictionsPlaceholder: transRestrictionsPlaceholder(trans),
		transProjectBusinessPlan: transProjectBusinessPlan(trans),
		getSemaphoreTitle: getSemaphoreTitle(trans),
		formatCredits: formatCredits(trans),
		formatPrice,
		formatPennyPrice,
		formatPricePerCredit,
		transBillingMethod: transBillingMethod(trans),
		transBillingMethodShort: transBillingMethodShort(trans),
		transBillingHistoryState: transBillingHistoryState(trans),
		transJournalAction: transJournalAction(trans),
		transHistoryType: transHistoryType(trans),
		filterSearchProjectsDesc: filterSearchProjectsDesc(trans),
		filterCatalogsDesc: filterCatalogsDesc(trans),
		bussinessPlanToShortcut: bussinessPlanToShortcut(),
		paymentMethodToShortcut: paymentMethodToShortcut(),
		getVIPInfo: getVIPInfo(),
		graphLegendTrans: graphLegendTrans(),
		transApprovalReason: transApprovalReason(),
		transCatalogQuality: transCatalogQuality(trans),
		transCatalogApproved: transCatalogApproved(trans),
		transQuickCommentType: transQuickCommentType(trans),
	};
}

/* exporty */
export function useNonHookTrans() {
	const langData = langStore.getState().data;
	const trans = getTrans(langData.trans);

	return getExport(trans);
}

export function useTrans() {
	const { langData } = langStore(langState => ({
		langData: langState.data,
	}));
	const trans = getTrans(langData.trans);

	return getExport(trans);
}
